import React, { useEffect, useState, useRef } from "react";
import Calling from '../../api/Calling';
import { toast } from "react-toastify";
import * as echarts from 'echarts';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import { Skeleton, Button } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    CalendarDays,
    CalendarRange,
    TrendingUp,
    Activity,
    Phone,
    PhoneMissed,
    PhoneOff,
    Box,
    Search,
    Filter,
    AlertCircle,
    XCircle,
    Download,
    RefreshCw,
    PhoneCall,
    ArrowLeft,
    ArrowRight,
    ChevronDown,
    Check,
    PieChart
} from "lucide-react";
import getISTDate from "../../components/getISTDate";
import formatDate from "../../components/formatDate";

// Brand palette
const BRAND = '#34C759';
const BRAND_HOVER = '#2db84d';
const BRAND_SOFT = 'rgba(52,199,89,0.10)';
const BRAND_RING = 'rgba(52,199,89,0.45)';

// Premium green-accent outlined field styling for the date pickers
const dateFieldSx = {
    minWidth: 168,
    '& .MuiInputLabel-root': { fontSize: '0.8rem', fontWeight: 500, color: '#6b7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#111827',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_RING },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${BRAND_SOFT}` },
    },
    // Brand the open-picker (calendar) icon button
    '& .MuiInputAdornment-root .MuiIconButton-root': {
        color: '#9ca3af',
        borderRadius: '8px',
        transition: 'color .15s ease, background-color .15s ease',
        '&:hover': { color: BRAND, backgroundColor: BRAND_SOFT },
    },
};
const applyBtnSx = {
    backgroundColor: BRAND,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 6px 16px -6px rgba(52,199,89,0.6)',
    whiteSpace: 'nowrap',
    px: 2.75,
    height: 40,
    '&:hover': { backgroundColor: BRAND_HOVER, boxShadow: '0 8px 20px -6px rgba(52,199,89,0.7)' },
};

// Premium, fully-branded theme for the calendar popup.
// All component keys & style slots below are verified to exist in @mui/x-date-pickers@9.6.0.
const pickerTheme = createTheme({
    palette: { primary: { main: BRAND } },
    shape: { borderRadius: 12 },
    typography: { fontWeightMedium: 600 },
    components: {
        // Popup surface (paper) — rounded + soft premium shadow
        MuiPickerPopper: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    marginTop: 6,
                    border: '1px solid #eef0f2',
                    boxShadow: '0 18px 50px -12px rgba(16,24,40,0.22), 0 8px 16px -8px rgba(16,24,40,0.10)',
                    overflow: 'hidden',
                },
            },
        },
        // Calendar header: month/year label + nav area
        MuiPickersCalendarHeader: {
            styleOverrides: {
                root: { paddingLeft: 18, paddingRight: 14, marginTop: 12, marginBottom: 4 },
                label: { fontWeight: 700, fontSize: '0.95rem', color: '#111827' },
                switchViewButton: {
                    color: '#374151',
                    '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND },
                },
            },
        },
        // Nav arrows (prev / next month)
        MuiPickersArrowSwitcher: {
            styleOverrides: {
                button: {
                    color: '#6b7280',
                    borderRadius: 10,
                    '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND },
                },
            },
        },
        // Weekday labels (Mo, Tu, …)
        MuiDayCalendar: {
            styleOverrides: {
                weekDayLabel: {
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    color: '#9ca3af',
                    textTransform: 'uppercase',
                },
            },
        },
        // Individual day cells
        MuiPickerDay: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: 10,
                    transition: 'background-color .12s ease, color .12s ease',
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&:focus': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        boxShadow: '0 4px 10px -3px rgba(52,199,89,0.6)',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
                today: {
                    border: `1.5px solid ${BRAND}`,
                    color: BRAND,
                    fontWeight: 700,
                    '&:not(.Mui-selected)': { backgroundColor: BRAND_SOFT },
                },
            },
        },
        // Year selection grid
        MuiYearCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
            },
        },
        // Month selection grid
        MuiMonthCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
            },
        },
    },
});

// Shared slots for both pickers — branded lucide icons (verified slot names for v9.6)
const pickerSlots = {
    openPickerIcon: () => <CalendarDays size={18} />,
    leftArrowIcon: () => <ArrowLeft size={18} />,
    rightArrowIcon: () => <ArrowRight size={18} />,
    switchViewIcon: () => <ChevronDown size={18} />,
};

const QUICK_RANGES = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'last6Months', label: 'Last 6 Months' },
    { key: 'thisYear', label: 'This Year' },
];

const ShowBoxTrack = () => {
    const [data, setData] = useState([]);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [loading, setLoading] = useState(false);
    const [selectedBtn, setSelectedBtn] = useState('today');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [selectedEventFilter, setSelectedEventFilter] = useState('all');

    // Draft (uncommitted) date-range values for the pickers — applied only on "Apply" click
    const [draftStart, setDraftStart] = useState(dayjs(getISTDate()));
    const [draftEnd, setDraftEnd] = useState(dayjs(getISTDate()));

    // ECharts refs — each chart gets its OWN ref + its OWN instance
    const lineChartRef = useRef(null);
    const lineChartInstance = useRef(null);
    const donutChartRef = useRef(null);
    const donutChartInstance = useRef(null);
    const barChartRef = useRef(null);
    const barChartInstance = useRef(null);

    // Event types configuration - dailing_box moved to first position
    const eventTypes = ['dailing_box', 'ring_box', 'miss_box', 'end_box', 'search_box'];

    const eventConfig = {
        dailing_box: {
            label: 'Dailing Box',
            icon: PhoneCall,
            color: '#34C759',
            bgColor: 'bg-emerald-50',
            textColor: 'text-main-blue',
            gradient: 'from-main-blue to-emerald-600',
            borderColor: 'border-main-blue',
            lightBg: '#f2fcf5'
        },
        ring_box: {
            label: 'Ring Box',
            icon: Phone,
            color: '#007bff',
            bgColor: 'bg-blue-50',
            textColor: 'text-main-exBlue',
            gradient: 'from-main-exBlue to-blue-600',
            borderColor: 'border-main-exBlue',
            lightBg: '#f0f7ff'
        },
        miss_box: {
            label: 'Miss Box',
            icon: PhoneMissed,
            color: '#F34235',
            bgColor: 'bg-red-50',
            textColor: 'text-main-red',
            gradient: 'from-main-red to-red-600',
            borderColor: 'border-main-red',
            lightBg: '#fff5f4'
        },
        end_box: {
            label: 'End Box',
            icon: PhoneOff,
            color: '#10b981',
            bgColor: 'bg-teal-50',
            textColor: 'text-teal-600',
            gradient: 'from-teal-500 to-teal-600',
            borderColor: 'border-teal-400',
            lightBg: '#f0fdfa'
        },
        search_box: {
            label: 'Search Box',
            icon: Search,
            color: '#FFC107',
            bgColor: 'bg-amber-50',
            textColor: 'text-main-yellow',
            gradient: 'from-main-yellow to-amber-500',
            borderColor: 'border-main-yellow',
            lightBg: '#fffbeb'
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await Calling.getShowboxAnalytic(startDate, endDate);
            console.log('API Response:', response);
            if (response?.status) {
                setData(response?.data || []);
            } else {
                toast.error(response?.message || "Failed to fetch ShowBox data");
            }
        } catch (error) {
            console.error("Error fetching ShowBox data:", error);
            toast.error("Server error while fetching ShowBox data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, selectedBtn]);

    // Keep the picker drafts in sync whenever the committed range changes (quick buttons / apply)
    useEffect(() => {
        setDraftStart(startDate ? dayjs(startDate) : null);
        setDraftEnd(endDate ? dayjs(endDate) : null);
    }, [startDate, endDate]);

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectedBtn('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectedBtn('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectedBtn('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectedBtn('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectedBtn('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);
                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectedBtn('thisYear');
                break;

            case 'lastYear':
                const firstDayLastYearUTC = new Date(todayUTC.getFullYear() - 1, 0, 1);
                const lastDayLastYearUTC = new Date(todayUTC.getFullYear() - 1, 11, 31);
                start = toIST(firstDayLastYearUTC);
                end = toIST(lastDayLastYearUTC);
                setSelectedBtn('lastYear');
                break;

            default:
                start = '';
                end = '';
                setSelectedBtn('range');
        }

        setStartDate(start);
        setEndDate(end);
        // keep picker drafts aligned with the quick-range commit so the Apply button doesn't flash
        setDraftStart(start ? dayjs(start) : null);
        setDraftEnd(end ? dayjs(end) : null);
    };

    // Commit the picked draft range — this is the ONLY thing that applies a custom range
    const handleApplyDateRange = () => {
        if (!draftStart || !draftStart.isValid() || !draftEnd || !draftEnd.isValid()) {
            toast.error('Please select both start and end dates', { autoClose: 2000 });
            return;
        }
        const today = dayjs().endOf('day');
        if (draftStart.isAfter(today) || draftEnd.isAfter(today)) {
            toast.error('Future dates are not allowed', { autoClose: 2000 });
            return;
        }
        if (draftStart.isAfter(draftEnd, 'day')) {
            toast.error('Start date cannot be after end date', { autoClose: 2000 });
            return;
        }
        setStartDate(draftStart.format('YYYY-MM-DD'));
        setEndDate(draftEnd.format('YYYY-MM-DD'));
        setSelectedBtn('range');
    };

    const handleDeleteData = async () => {
        if (!startDate || !endDate) {
            toast.error("Please select a valid date range");
            return;
        }

        setDeleteLoading(true);
        try {
            const response = await Calling.deleteShowboxAnalytic(startDate, endDate);

            if (response?.status) {
                toast.success(response?.message || "Data deleted successfully");
                fetchData();
                setShowDeleteModal(false);
            } else {
                toast.error(response?.message || "Failed to delete data");
            }
        } catch (error) {
            console.error("Error deleting data:", error);
            toast.error("Server error while deleting data");
        } finally {
            setDeleteLoading(false);
        }
    };

    // Get filtered data based on selected event filter
    const getFilteredData = () => {
        if (selectedEventFilter === 'all') return data;
        return data.filter(item => item.a_event === selectedEventFilter);
    };

    const filteredData = getFilteredData();

    // Calculate analytics
    const getAnalytics = () => {
        const analytics = {
            total: 0,
            dailing_box: 0,
            ring_box: 0,
            miss_box: 0,
            end_box: 0,
            search_box: 0
        };

        data.forEach(item => {
            const count = item.a_count || 0;
            analytics.total += count;
            if (analytics[item.a_event] !== undefined) {
                analytics[item.a_event] += count;
            }
        });

        return analytics;
    };

    const analytics = getAnalytics();

    // Group data by date for chart
    const getChartData = () => {
        const eventsByDate = {};

        filteredData.forEach((item) => {
            const date = formatDate(item.createdAt);

            if (!eventsByDate[date]) {
                eventsByDate[date] = {};
            }
            if (!eventsByDate[date][item.a_event]) {
                eventsByDate[date][item.a_event] = 0;
            }
            eventsByDate[date][item.a_event] += item.a_count || 0;
        });

        const labels = Object.keys(eventsByDate).sort((a, b) => new Date(a) - new Date(b));
        const activeEventTypes = selectedEventFilter === 'all'
            ? eventTypes
            : [selectedEventFilter];

        return { eventsByDate, labels, activeEventTypes };
    };

    const { eventsByDate, labels: chartLabels, activeEventTypes } = getChartData();

    // Donut/bar are driven by the full `analytics` breakdown (independent of line-chart filter)
    const hasAnalytics = analytics.total > 0;

    // ===== LINE / AREA TIMELINE CHART =====
    useEffect(() => {
        if (!lineChartRef.current || filteredData.length === 0) return;

        // If a stale instance is bound to a detached/old DOM node (e.g. after a
        // loading toggle remounted the container), dispose it before re-init so
        // ECharts paints on the live node and never throws "dom already initialized".
        if (lineChartInstance.current && lineChartInstance.current.getDom() !== lineChartRef.current) {
            lineChartInstance.current.dispose();
            lineChartInstance.current = null;
        }
        if (!lineChartInstance.current) {
            lineChartInstance.current = echarts.init(lineChartRef.current);
        }
        const chart = lineChartInstance.current;

        const series = activeEventTypes.map((eventType) => {
            const config = eventConfig[eventType];
            const color = config?.color || '#9ca3af';

            return {
                name: config?.label || eventType,
                type: 'line',
                smooth: 0.5,
                smoothMonotone: 'x',
                symbol: 'circle',
                symbolSize: 7,
                showSymbol: false,
                sampling: 'lttb',
                lineStyle: {
                    width: 3,
                    color: color,
                    shadowColor: color + '55',
                    shadowBlur: 8,
                    shadowOffsetY: 4,
                    cap: 'round'
                },
                itemStyle: {
                    color: color,
                    borderColor: '#fff',
                    borderWidth: 2
                },
                areaStyle: {
                    origin: 'start',
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: color + '38' },
                        { offset: 0.7, color: color + '12' },
                        { offset: 1, color: color + '02' }
                    ])
                },
                emphasis: {
                    focus: 'series',
                    itemStyle: {
                        borderWidth: 3,
                        shadowBlur: 12,
                        shadowColor: color + '70'
                    }
                },
                data: chartLabels.map(date => eventsByDate[date]?.[eventType] || 0)
            };
        });

        const option = {
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#111723',
                borderColor: 'transparent',
                borderRadius: 12,
                padding: [12, 16],
                textStyle: {
                    color: '#fff',
                    fontSize: 13
                },
                axisPointer: {
                    type: 'cross',
                    crossStyle: {
                        color: '#CFD1D3'
                    },
                    lineStyle: {
                        color: '#CFD1D3',
                        type: 'dashed'
                    }
                },
                formatter: function (params) {
                    let result = `<div style="font-weight: 600; margin-bottom: 10px; font-size: 14px;">${params[0].axisValue}</div>`;
                    params.forEach(param => {
                        result += `<div style="display: flex; align-items: center; justify-content: space-between; margin: 6px 0;">
                            <span style="display: flex; align-items: center;">
                                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${param.color}; margin-right: 10px;"></span>
                                <span style="color: #888B91;">${param.seriesName}</span>
                            </span>
                            <span style="font-weight: 700; margin-left: 24px; color: #fff;">${param.value.toLocaleString()}</span>
                        </div>`;
                    });
                    return result;
                }
            },
            legend: {
                type: 'scroll',
                top: 0,
                left: 'center',
                icon: 'roundRect',
                itemWidth: 14,
                itemHeight: 8,
                borderRadius: 4,
                itemGap: 24,
                textStyle: {
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#111723'
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                top: '15%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: chartLabels,
                axisLine: {
                    lineStyle: {
                        color: '#E5E7EB'
                    }
                },
                axisLabel: {
                    color: '#888B91',
                    fontSize: 11,
                    rotate: chartLabels.length > 10 ? 45 : 0
                },
                axisTick: {
                    show: false
                }
            },
            yAxis: {
                type: 'value',
                minInterval: 1,
                axisLine: {
                    show: false
                },
                axisLabel: {
                    color: '#888B91',
                    fontSize: 11
                },
                splitLine: {
                    lineStyle: {
                        color: '#EEF0F2',
                        type: 'dashed'
                    }
                }
            },
            series: series
        };

        chart.setOption(option, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredData, chartLabels, activeEventTypes, selectedEventFilter, eventsByDate]);

    // ===== DONUT / RING — EVENT TYPE DISTRIBUTION (driven by full analytics) =====
    useEffect(() => {
        if (!donutChartRef.current || !hasAnalytics) return;

        if (donutChartInstance.current && donutChartInstance.current.getDom() !== donutChartRef.current) {
            donutChartInstance.current.dispose();
            donutChartInstance.current = null;
        }
        if (!donutChartInstance.current) {
            donutChartInstance.current = echarts.init(donutChartRef.current);
        }
        const chart = donutChartInstance.current;

        const donutData = eventTypes
            .map((type) => ({
                name: eventConfig[type].label,
                value: analytics[type] || 0,
                itemStyle: { color: eventConfig[type].color }
            }))
            .filter((d) => d.value > 0);

        const option = {
            tooltip: {
                trigger: 'item',
                backgroundColor: '#111723',
                borderColor: 'transparent',
                borderRadius: 12,
                padding: [10, 14],
                textStyle: { color: '#fff', fontSize: 13 },
                formatter: (p) =>
                    `<div style="display:flex;align-items:center;">
                        <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:10px;"></span>
                        <span style="color:#888B91;">${p.name}</span>
                        <span style="font-weight:700;margin-left:18px;color:#fff;">${p.value.toLocaleString()} (${p.percent}%)</span>
                    </div>`
            },
            legend: {
                bottom: 0,
                left: 'center',
                icon: 'circle',
                itemWidth: 8,
                itemHeight: 8,
                itemGap: 16,
                textStyle: { fontSize: 12, color: '#888B91', fontWeight: 500 }
            },
            graphic: {
                type: 'group',
                left: 'center',
                top: '38%',
                children: [
                    {
                        type: 'text',
                        style: {
                            text: analytics.total.toLocaleString(),
                            fontSize: 30,
                            fontWeight: 700,
                            fill: '#111723',
                            textAlign: 'center'
                        }
                    },
                    {
                        type: 'text',
                        top: 38,
                        style: {
                            text: 'Total Events',
                            fontSize: 12,
                            fontWeight: 500,
                            fill: '#888B91',
                            textAlign: 'center'
                        }
                    }
                ]
            },
            series: [
                {
                    name: 'Event Distribution',
                    type: 'pie',
                    radius: ['58%', '78%'],
                    center: ['50%', '44%'],
                    avoidLabelOverlap: true,
                    padAngle: 3,
                    itemStyle: {
                        borderRadius: 8,
                        borderColor: '#fff',
                        borderWidth: 2
                    },
                    label: { show: false },
                    labelLine: { show: false },
                    emphasis: {
                        scaleSize: 6,
                        itemStyle: {
                            shadowBlur: 14,
                            shadowColor: 'rgba(0,0,0,0.18)'
                        }
                    },
                    data: donutData
                }
            ]
        };

        chart.setOption(option, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, hasAnalytics]);

    // ===== HORIZONTAL BAR — 5 EVENT TOTALS COMPARISON =====
    useEffect(() => {
        if (!barChartRef.current || !hasAnalytics) return;

        if (barChartInstance.current && barChartInstance.current.getDom() !== barChartRef.current) {
            barChartInstance.current.dispose();
            barChartInstance.current = null;
        }
        if (!barChartInstance.current) {
            barChartInstance.current = echarts.init(barChartRef.current);
        }
        const chart = barChartInstance.current;

        // Largest at top for a clean ranking look
        const sorted = [...eventTypes].sort((a, b) => (analytics[a] || 0) - (analytics[b] || 0));

        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                backgroundColor: '#111723',
                borderColor: 'transparent',
                borderRadius: 12,
                padding: [10, 14],
                textStyle: { color: '#fff', fontSize: 13 },
                formatter: (params) => {
                    const p = params[0];
                    return `<span style="color:#888B91;">${p.name}</span>
                        <span style="font-weight:700;margin-left:18px;color:#fff;">${p.value.toLocaleString()}</span>`;
                }
            },
            grid: { left: '2%', right: '6%', top: '6%', bottom: '2%', containLabel: true },
            xAxis: {
                type: 'value',
                minInterval: 1,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { color: '#888B91', fontSize: 11 },
                splitLine: { lineStyle: { color: '#EEF0F2', type: 'dashed' } }
            },
            yAxis: {
                type: 'category',
                data: sorted.map((t) => eventConfig[t].label),
                axisLine: { lineStyle: { color: '#E5E7EB' } },
                axisTick: { show: false },
                axisLabel: { color: '#111723', fontSize: 12, fontWeight: 500 }
            },
            series: [
                {
                    type: 'bar',
                    barWidth: '52%',
                    itemStyle: {
                        borderRadius: [0, 8, 8, 0],
                        color: (params) => eventConfig[sorted[params.dataIndex]].color
                    },
                    label: {
                        show: true,
                        position: 'right',
                        color: '#111723',
                        fontWeight: 700,
                        fontSize: 12,
                        formatter: (p) => p.value.toLocaleString()
                    },
                    data: sorted.map((t) => analytics[t] || 0)
                }
            ]
        };

        chart.setOption(option, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, hasAnalytics]);

    // ===== ONE shared resize listener + dispose-all on unmount =====
    useEffect(() => {
        const handleResize = () => {
            lineChartInstance.current?.resize();
            donutChartInstance.current?.resize();
            barChartInstance.current?.resize();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            lineChartInstance.current?.dispose();
            donutChartInstance.current?.dispose();
            barChartInstance.current?.dispose();
            lineChartInstance.current = null;
            donutChartInstance.current = null;
            barChartInstance.current = null;
        };
    }, []);

    // Dispose individual charts when their container unmounts (empty state)
    useEffect(() => {
        if (filteredData.length === 0 && lineChartInstance.current) {
            lineChartInstance.current.dispose();
            lineChartInstance.current = null;
        }
    }, [filteredData.length]);

    useEffect(() => {
        if (!hasAnalytics) {
            if (donutChartInstance.current) {
                donutChartInstance.current.dispose();
                donutChartInstance.current = null;
            }
            if (barChartInstance.current) {
                barChartInstance.current.dispose();
                barChartInstance.current = null;
            }
        }
    }, [hasAnalytics]);

    // Export to CSV
    const handleExportCSV = () => {
        try {
            if (filteredData.length === 0) {
                toast.warning("No data available to export");
                return;
            }

            const exportData = filteredData.map((item, index) => ({
                '#': index + 1,
                'Event Type': eventConfig[item.a_event]?.label || item.a_event,
                'Count': item.a_count,
                'Created At': formatDate(item.createdAt),
                'Updated At': formatDate(item.updatedAt),
            }));

            const headers = Object.keys(exportData[0]).join(',');
            const rows = exportData.map(row =>
                Object.values(row).map(value =>
                    typeof value === 'string' && (value.includes(',') || value.includes('"'))
                        ? `"${value.replace(/"/g, '""')}"`
                        : value
                ).join(',')
            );

            const csv = [headers, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.setAttribute('href', url);
            link.setAttribute('download', `ShowBoxAnalytics_${startDate}_to_${endDate}.csv`);
            link.style.visibility = 'hidden';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success("CSV file downloaded successfully");
        } catch (error) {
            console.error("Error exporting CSV:", error);
            toast.error("Failed to export CSV");
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className='w-full min-h-screen text-main-font'
        >
            {/* Page Header */}
            <div className="mb-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-main-blue to-emerald-600 flex items-center justify-center text-main-white shadow-lg shadow-main-blue/30">
                        <Box size={26} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font tracking-tight">ShowBox Analytics</h1>
                        <p className="text-sm text-main-subfont mt-1">Track and analyze your ShowBox events in real-time</p>
                    </div>
                </div>
            </div>
            <div className="bg-main-bg p-4 rounded-xl">

                {/* Date Range Selection Card */}
                <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden mb-6">
                    <div className="p-5">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 bg-main-blue/10 rounded-xl flex items-center justify-center">
                                <CalendarDays className="text-main-blue" size={20} />
                            </div>
                            <div>
                                <h2 className="text-base font-semibold text-main-font">Date Range</h2>
                                <p className="text-xs text-main-subfont">Select a time period to analyze</p>
                            </div>
                        </div>

                        <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
                            {/* Quick Range — modern segmented control */}
                            <div>
                                <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                    <CalendarDays size={13} /> Quick Range
                                </p>
                                <div className="inline-flex flex-wrap items-center gap-1 bg-main-bg border border-main-border rounded-xl p-1">
                                    {QUICK_RANGES.map((r) => {
                                        const active = selectedBtn === r.key;
                                        return (
                                            <button
                                                key={r.key}
                                                type="button"
                                                onClick={() => handleDateRangeFilter(r.key)}
                                                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${active
                                                    ? 'bg-main-blue text-main-white shadow-sm shadow-main-blue/30'
                                                    : 'text-main-subfont hover:text-main-font hover:bg-main-white'
                                                    }`}
                                            >
                                                {r.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Custom Range — themed MUI date pickers + Apply (applies only on click) */}
                            <div>
                                <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                    <CalendarRange size={13} /> Custom Range
                                </p>
                                <ThemeProvider theme={pickerTheme}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 bg-main-bg border border-main-border rounded-2xl p-2.5 shadow-sm">
                                            <DatePicker
                                                label="From"
                                                value={draftStart}
                                                onChange={(v) => setDraftStart(v)}
                                                format="DD MMM YYYY"
                                                disableFuture
                                                maxDate={draftEnd || undefined}
                                                slots={pickerSlots}
                                                slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                            />
                                            <span className="hidden sm:flex items-center justify-center text-main-subfont">
                                                <ArrowRight size={16} />
                                            </span>
                                            <DatePicker
                                                label="To"
                                                value={draftEnd}
                                                onChange={(v) => setDraftEnd(v)}
                                                format="DD MMM YYYY"
                                                disableFuture
                                                minDate={draftStart || undefined}
                                                slots={pickerSlots}
                                                slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={handleApplyDateRange}
                                                sx={{ ...applyBtnSx, display: ((draftStart && draftStart.isValid() ? draftStart.format('YYYY-MM-DD') : '') !== (startDate || '') || (draftEnd && draftEnd.isValid() ? draftEnd.format('YYYY-MM-DD') : '') !== (endDate || '')) ? 'inline-flex' : 'none' }}
                                                startIcon={<Check size={16} />}
                                            >
                                                Apply
                                            </Button>
                                        </div>
                                    </LocalizationProvider>
                                </ThemeProvider>
                            </div>
                        </div>

                        {/* Applied-range indicator */}
                        {selectedBtn === 'range' && startDate && endDate && (
                            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-main-th border border-main-blue/20 rounded-lg">
                                <CalendarRange size={14} className="text-main-blue" />
                                <span className="text-xs text-main-subfont">
                                    Showing <span className="font-semibold text-main-font">{formatDate(startDate)}</span> → <span className="font-semibold text-main-font">{formatDate(endDate)}</span>
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Event Type Filter */}
                    <div className="px-5 py-4 border-t border-main-border bg-main-th/50">
                        <div className="flex items-start justify-between gap-4 flex-wrap">

                            {/* LEFT : Filters */}
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="text-sm font-semibold text-main-font">Filter:</span>

                                <button
                                    onClick={() => setSelectedEventFilter('all')}
                                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${selectedEventFilter === 'all'
                                        ? 'bg-main-blue text-main-white shadow-lg shadow-main-blue/25'
                                        : 'bg-main-white text-main-subfont border border-main-border hover:border-main-blue/50 hover:text-main-blue'
                                        }`}
                                >
                                    All Events
                                </button>

                                {eventTypes.map((type) => {
                                    const config = eventConfig[type];
                                    const Icon = config.icon;
                                    return (
                                        <button
                                            key={type}
                                            onClick={() => setSelectedEventFilter(type)}
                                            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${selectedEventFilter === type
                                                ? `${config.bgColor} ${config.textColor} border-2 ${config.borderColor}`
                                                : 'bg-main-white text-main-subfont border border-main-border hover:border-main-blue/50'
                                                }`}
                                        >
                                            <Icon size={16} />
                                            {config.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* RIGHT : Action Buttons */}
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={fetchData}
                                    disabled={loading}
                                    className="px-5 py-2.5 bg-main-white text-main-font rounded-xl font-semibold hover:bg-main-th transition-all flex items-center gap-2 text-sm border border-main-border disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Refresh data"
                                >
                                    <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                                </button>

                                <button
                                    onClick={handleExportCSV}
                                    className="px-5 py-2.5 bg-main-blue text-main-white rounded-xl font-semibold hover:bg-emerald-600 transition-all flex items-center gap-2 text-sm shadow-lg shadow-main-blue/25"
                                    title="Export CSV"
                                >
                                    <Download size={18} />
                                </button>

                                <button
                                    onClick={() => setShowDeleteModal(true)}
                                    className="px-5 py-2.5 bg-main-red text-main-white rounded-xl font-semibold hover:bg-red-600 transition-all flex items-center gap-2 text-sm shadow-lg shadow-main-red/25"
                                    title="Delete data"
                                >
                                    <XCircle size={18} />
                                </button>
                            </div>

                        </div>
                    </div>

                </div>

                {/* Delete Modal */}
                <AnimatePresence>
                    {showDeleteModal && (
                        <motion.div
                            key="showbox-delete-modal"
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="bg-main-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                                initial={{ scale: 0.92, y: 16, opacity: 0 }}
                                animate={{ scale: 1, y: 0, opacity: 1 }}
                                exit={{ scale: 0.92, y: 16, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                {/* Modal Header */}
                                <div className="bg-main-red/10 p-5">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 bg-main-red/20 rounded-2xl flex items-center justify-center">
                                            <AlertCircle className="text-main-red" size={30} />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-main-font">Delete ShowBox Data</h3>
                                            <p className="text-sm text-main-subfont mt-0.5">This action cannot be undone</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-5">
                                    <div className="space-y-4">
                                        <p className="text-main-subfont text-sm">
                                            You are about to permanently delete ShowBox analytics data for the selected period.
                                        </p>

                                        <div className="bg-main-bg rounded-xl p-4 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-main-subfont">Date Range</span>
                                                <span className="text-sm font-semibold text-main-font">
                                                    {startDate} → {endDate}
                                                </span>
                                            </div>
                                            <div className="h-px bg-main-border"></div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-main-subfont">Records to Delete</span>
                                                <span className="text-sm font-bold text-main-red">
                                                    {data.length} records
                                                </span>
                                            </div>
                                        </div>

                                        <div className="bg-main-yellow/10 border border-main-yellow/30 rounded-xl p-4">
                                            <p className="text-sm text-main-font font-medium flex items-start gap-2">
                                                <span className="text-main-yellow text-lg">⚠</span>
                                                <span>All data matching this date range will be permanently removed from the system.</span>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex gap-3 mt-6">
                                        <button
                                            onClick={() => setShowDeleteModal(false)}
                                            disabled={deleteLoading}
                                            className="flex-1 px-5 py-3 bg-main-bg text-main-font rounded-xl font-semibold hover:bg-gray-100 transition-all disabled:opacity-50 border border-main-border"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleDeleteData}
                                            disabled={deleteLoading}
                                            className="flex-1 px-5 py-3 bg-main-red text-main-white rounded-xl font-semibold hover:bg-red-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-main-red/25"
                                        >
                                            {deleteLoading ? (
                                                <>
                                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    <span>Deleting...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <XCircle size={18} />
                                                    <span>Delete Data</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Total Events Card */}
                <div className="mb-6">
                    <div className="bg-gradient-to-br from-main-blue to-emerald-600 rounded-2xl shadow-xl shadow-main-blue/20 p-6 text-main-white relative overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
                            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
                        </div>

                        <div className="relative flex items-center justify-between">
                            <div>
                                <p className="text-emerald-100 text-sm font-medium uppercase tracking-widest mb-2">Total Events</p>
                                {loading ? (
                                    <Skeleton variant="text" width={180} height={64} sx={{ bgcolor: 'rgba(255,255,255,0.25)' }} />
                                ) : (
                                    <p className="text-5xl font-bold tracking-tight">{analytics.total.toLocaleString()}</p>
                                )}
                                <div className="flex items-center gap-2 mt-3 text-emerald-100">
                                    <CalendarDays size={14} />
                                    <span className="text-sm font-medium">{startDate} → {endDate}</span>
                                </div>
                            </div>
                            <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center">
                                <Activity size={48} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-10 h-10 bg-main-exBlue/10 rounded-xl flex items-center justify-center">
                            <TrendingUp className="text-main-exBlue" size={20} />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-main-font">Event Summary</h3>
                            <p className="text-xs text-main-subfont">Breakdown by event type</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {loading
                            ? eventTypes.map((eventType) => (
                                <div key={eventType} className="bg-main-white rounded-2xl border-2 border-main-border overflow-hidden">
                                    <Skeleton variant="rectangular" height={4} />
                                    <div className="p-5">
                                        <div className="flex items-start justify-between mb-4">
                                            <Skeleton variant="rounded" width={48} height={48} />
                                            <Skeleton variant="rounded" width={42} height={22} />
                                        </div>
                                        <Skeleton variant="text" width={80} height={16} />
                                        <Skeleton variant="text" width={64} height={40} />
                                    </div>
                                </div>
                            ))
                            : eventTypes.map((eventType, idx) => {
                                const config = eventConfig[eventType];
                                const Icon = config.icon;
                                const value = analytics[eventType] || 0;
                                const percentage = ((value / analytics.total) * 100 || 0).toFixed(1);

                                return (
                                    <motion.div
                                        key={eventType}
                                        initial={{ opacity: 0, y: 14 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: idx * 0.06 }}
                                        onClick={() => setSelectedEventFilter(eventType)}
                                        className={`bg-main-white rounded-2xl border-2 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group ${selectedEventFilter === eventType
                                            ? `${config.borderColor} shadow-lg`
                                            : 'border-main-border hover:border-main-blue/30'
                                            }`}
                                    >
                                        <div className={`h-1 bg-gradient-to-r ${config.gradient}`}></div>
                                        <div className="p-5">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all group-hover:scale-110" style={{ backgroundColor: config.lightBg }}>
                                                    <Icon className={config.textColor} size={24} />
                                                </div>
                                                <span className={`text-xs font-bold ${config.textColor} px-2.5 py-1 rounded-lg`} style={{ backgroundColor: config.lightBg }}>
                                                    {percentage}%
                                                </span>
                                            </div>
                                            <p className="text-xs font-semibold text-main-subfont uppercase tracking-wide mb-2">
                                                {config.label}
                                            </p>
                                            <p className="text-3xl font-bold text-main-font tracking-tight">
                                                {value.toLocaleString()}
                                            </p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                    </div>
                </div>

                {/* Charts Row: Donut distribution + Bar comparison */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Donut — Event Type Distribution */}
                    <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                        <div className="p-5 border-b border-main-border">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-main-blue/10 rounded-xl flex items-center justify-center">
                                    <PieChart className="text-main-blue" size={20} />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-main-font">Event Distribution</h2>
                                    <p className="text-xs text-main-subfont mt-0.5">Share of each event type</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-5 relative" style={{ minHeight: 360 }}>
                            {/* ECharts mount stays ALWAYS rendered (never conditionally unmounted)
                                so React never removes a node ECharts has mutated → no removeChild crash.
                                Skeleton / empty-state render as overlays on top. */}
                            <div
                                ref={donutChartRef}
                                style={{ width: '100%', height: 360, opacity: !loading && hasAnalytics ? 1 : 0, transition: 'opacity .2s' }}
                            />
                            {loading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-main-white">
                                    <Skeleton variant="circular" width={220} height={220} />
                                </div>
                            )}
                            {!loading && !hasAnalytics && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center bg-main-white text-main-subfont">
                                    <div className="w-20 h-20 bg-main-bg rounded-full flex items-center justify-center mb-5">
                                        <PieChart size={36} />
                                    </div>
                                    <p className="text-lg font-semibold text-main-font">No events found</p>
                                    <p className="text-sm mt-1">Try selecting a different date range</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bar — Event Totals Comparison */}
                    <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                        <div className="p-5 border-b border-main-border">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-main-exBlue/10 rounded-xl flex items-center justify-center">
                                    <TrendingUp className="text-main-exBlue" size={20} />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-main-font">Event Comparison</h2>
                                    <p className="text-xs text-main-subfont mt-0.5">Totals by event type</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-5 relative" style={{ minHeight: 360 }}>
                            {/* Always-mounted ECharts node + overlays (see donut chart note above) */}
                            <div
                                ref={barChartRef}
                                style={{ width: '100%', height: 360, opacity: !loading && hasAnalytics ? 1 : 0, transition: 'opacity .2s' }}
                            />
                            {loading && (
                                <div className="absolute inset-0 p-5 space-y-4 bg-main-white">
                                    {eventTypes.map((t) => (
                                        <Skeleton key={t} variant="rounded" height={40} />
                                    ))}
                                </div>
                            )}
                            {!loading && !hasAnalytics && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center bg-main-white text-main-subfont">
                                    <div className="w-20 h-20 bg-main-bg rounded-full flex items-center justify-center mb-5">
                                        <TrendingUp size={36} />
                                    </div>
                                    <p className="text-lg font-semibold text-main-font">No events found</p>
                                    <p className="text-sm mt-1">Try selecting a different date range</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ECharts Area Chart Section */}
                <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden mb-6">
                    <div className="p-5 border-b border-main-border">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-main-yellow/10 rounded-xl flex items-center justify-center">
                                    <Activity className="text-main-yellow" size={20} />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-main-font">Events Timeline</h2>
                                    <p className="text-xs text-main-subfont mt-0.5">Track event trends over time</p>
                                </div>
                            </div>
                            <span className="text-xs font-semibold text-main-subfont bg-main-bg px-3 py-1.5 rounded-lg border border-main-border">
                                {filteredData.length} records
                            </span>
                        </div>
                    </div>
                    <div className="p-5 relative" style={{ minHeight: 400 }}>
                        {/* Always-mounted ECharts node + overlays (see donut chart note above) */}
                        <div
                            ref={lineChartRef}
                            style={{ width: '100%', height: 400, opacity: !loading && filteredData.length > 0 ? 1 : 0, transition: 'opacity .2s' }}
                        />
                        {loading && (
                            <div className="absolute inset-0 p-5 bg-main-white">
                                <Skeleton variant="rounded" width="100%" height={360} />
                            </div>
                        )}
                        {!loading && filteredData.length === 0 && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-main-white text-main-subfont">
                                <div className="w-20 h-20 bg-main-bg rounded-full flex items-center justify-center mb-5">
                                    <Activity size={36} />
                                </div>
                                <p className="text-lg font-semibold text-main-font">No events found</p>
                                <p className="text-sm mt-1">Try selecting a different date range or filter</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                    <div className="p-5 border-b border-main-border">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-main-exBlue/10 rounded-xl flex items-center justify-center">
                                    <Filter className="text-main-exBlue" size={20} />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-main-font">Event Records</h2>
                                    <p className="text-xs text-main-subfont mt-0.5">{filteredData.length} records found</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead>
                                <tr className="bg-main-th">
                                    <th className="px-6 py-4 text-left text-xs font-bold text-main-font uppercase tracking-wider">
                                        #
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-main-font uppercase tracking-wider">
                                        Event Type
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-main-font uppercase tracking-wider">
                                        Count
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-main-font uppercase tracking-wider">
                                        Created At
                                    </th>
                                    <th className="px-6 py-4 text-center text-xs font-bold text-main-font uppercase tracking-wider">
                                        Updated At
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-main-border">
                                {loading ? (
                                    Array.from({ length: 6 }).map((_, i) => (
                                        <tr key={i}>
                                            <td className="px-6 py-4"><Skeleton variant="rounded" width={32} height={32} /></td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <Skeleton variant="rounded" width={44} height={44} />
                                                    <Skeleton variant="text" width={90} />
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center"><Skeleton variant="text" width={40} sx={{ mx: 'auto' }} /></td>
                                            <td className="px-6 py-4 text-center"><Skeleton variant="text" width={90} sx={{ mx: 'auto' }} /></td>
                                            <td className="px-6 py-4 text-center"><Skeleton variant="text" width={90} sx={{ mx: 'auto' }} /></td>
                                        </tr>
                                    ))
                                ) : filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center">
                                                <div className="w-16 h-16 bg-main-bg rounded-full flex items-center justify-center mb-4">
                                                    <Box size={28} className="text-main-subfont" />
                                                </div>
                                                <p className="text-main-font font-semibold">No records found</p>
                                                <p className="text-sm text-main-subfont mt-1">Try adjusting your filters</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredData.map((item, i) => {
                                        const config = eventConfig[item.a_event] || {};
                                        const Icon = config.icon || Box;

                                        return (
                                            <tr
                                                key={item._id}
                                                className="hover:bg-main-thHover transition-colors"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm font-bold text-main-font bg-main-bg w-8 h-8 rounded-lg flex items-center justify-center">
                                                        {i + 1}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: config.lightBg || '#f3f4f6' }}>
                                                            <Icon className={config.textColor || 'text-gray-600'} size={20} />
                                                        </div>
                                                        <span className={`px-3 py-1.5 text-xs font-bold rounded-lg ${config.textColor || 'text-gray-700'}`} style={{ backgroundColor: config.lightBg || '#f3f4f6' }}>
                                                            {config.label || item.a_event}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className="text-2xl font-bold text-main-font">
                                                        {item.a_count?.toLocaleString() || 0}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className="text-sm font-medium text-main-subfont bg-main-bg px-3 py-1.5 rounded-lg">
                                                        {formatDate(item.createdAt)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className="text-sm font-medium text-main-subfont bg-main-bg px-3 py-1.5 rounded-lg">
                                                        {formatDate(item.updatedAt)}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default ShowBoxTrack;
